/**
 * Pure helpers for the increment/decrement steppers of {@link NumberInput}.
 *
 * Kept framework-free and side-effect-free so the stepping arithmetic can be
 * unit-tested in isolation. All values are plain numbers in the input's own
 * unit (for margin inputs that is cm); unit conversion happens at the UI
 * boundary, not here.
 */

/**
 * A step size. Either a constant, or an adaptive function of the current
 * value (e.g. finer steps near small margins, coarser near the maximum).
 */
export type StepResolver = number | ((value: number) => number);

/**
 * Absolute nudge (in value units) used to probe the band *just below* the
 * current value when stepping down, so that a downward step from a band
 * boundary uses the finer (lower) band's step rather than the coarser one.
 * Far smaller than any realistic step, so it never affects the chosen band
 * except exactly at a boundary.
 */
const STEP_PROBE_EPSILON = 1e-9;

/**
 * Tolerance, expressed in grid units, that absorbs floating-point dust when
 * deciding whether the current value already sits on a grid line. Negligible
 * compared to a single grid unit, but many orders of magnitude larger than
 * the ~1e-15 relative error of IEEE-754 division.
 */
const GRID_UNIT_EPSILON = 1e-6;

/** Resolve a {@link StepResolver} to a concrete step for the given value. */
export function resolveStep(step: StepResolver, value: number): number {
    return typeof step === 'function' ? step(value) : step;
}

/**
 * Compute the next value produced by an increment/decrement stepper press.
 *
 * When `snapToStep` is false the step is simply added (legacy behaviour:
 * `value ± step`). When true, the result is snapped to the step grid anchored
 * at `anchor`, so an off-grid typed value (e.g. 0.22 with a 0.05 step) lands
 * on the next grid line (0.25 up, 0.20 down) instead of accumulating the raw
 * step (0.27 / 0.17).
 *
 * The result is NOT clamped to min/max or rounded to display precision here;
 * the caller is expected to do that (see NumberInput's `clamp`).
 *
 * @param value current value
 * @param direction +1 to increment, -1 to decrement
 * @param step constant step, or adaptive step function
 * @param snapToStep snap the result to the step grid when true
 * @param anchor grid origin the step lattice is measured from (typically the
 *        input's `min`, or 0 when `min` is unbounded)
 */
export function computeSteppedValue(
    value: number,
    direction: 1 | -1,
    step: StepResolver,
    snapToStep: boolean,
    anchor: number
): number {
    // Probe just below the value on the way down so a step from a band
    // boundary picks the finer band (e.g. stepping down from 0.50 uses the
    // sub-0.50 step, not the >=0.50 step).
    const probe = direction > 0 ? value : value - STEP_PROBE_EPSILON;
    const effectiveStep = resolveStep(step, probe);

    if (!snapToStep) {
        return value + direction * effectiveStep;
    }

    const units = (value - anchor) / effectiveStep;
    const nextUnits =
        direction > 0
            ? Math.floor(units + GRID_UNIT_EPSILON) + 1
            : Math.ceil(units - GRID_UNIT_EPSILON) - 1;
    return anchor + nextUnits * effectiveStep;
}
